/**
 * 收藏效果组件
 * @author chenqunfeng
 * @since 18/05/04
 */

/**
 * 飞向某一个目的地的效果
 * @param {HTMLNode} start
 * @param {HTMLNode} end
 * @param {object} startOpt|endOpt
 * {
 *  isFixed,
 *  toFixed,
 *  initStyle,
 * }
 */
export async function flyTo(start, end, startOpt = {}, endOpt = {}) {
  if (start && end) {
    const startPos = getPosition(start, startOpt);
    const endPos = getPosition(end, endOpt);
    // 根据start和end的宽高调整实际目的地的x/y坐标
    endPos.x =
      endPos.x -
      getStyle(start, 'width').replace(/px/g, '') / 2 +
      getStyle(end, 'width').replace(/px/g, '') / 2;
    endPos.y =
      endPos.y -
      getStyle(start, 'height').replace(/px/g, '') / 2 +
      getStyle(end, 'height').replace(/px/g, '') / 2;
    const style = {
      position: 'fixed',
      top: `${startPos.y}px`,
      left: `${startPos.x}px`,
      // 避免插入元素后,某些hover效果会受到影响
      'pointer-events': 'none',
      ...startOpt.initStyle,
    };
    const startClone = cloneNode(start, style, true);
    await animation(cb => {
      const params = curve(startPos, endPos);
      const _getSpeed = getSpeed(startPos.x, endPos.x);
      const move = (_x, cb) => {
        let speed = _getSpeed(_x);
        if (_x <= endPos.x) {
          const _y = params.a * _x ** 2 + params.b * _x + params.c;
          css(startClone, { top: `${_y}px`, left: `${_x}px` });
          if (_x < endPos.x) {
            requestAnimationFrame(() => {
              move(_x + speed >= endPos.x ? endPos.x : _x + speed, cb);
            });
          } else {
            cb && cb();
          }
        } else {
          cb && cb();
        }
      };
      move && move(startPos.x, cb);
    }, 0);
    await animation(() => {
      css(startClone, {
        transform: `scale(0)`,
        transition: `all 0.2s`,
      });
    });
    await animation(() => {
      document.body.removeChild(startClone);
    }, 200);
  }
}

/**
 * 获取速度值
 * @param {number} start 起始点x或y坐标
 * @param {number} end   结束点x或y坐标
 * @returns {function}
 */
export function getSpeed(start, end) {
  // 初始速度
  let speed = 1;
  const block = 40;
  // 速度上限
  const limit = parseInt(Math.abs(end - start) / block);
  return function(x) {
    if (Math.abs(x - end) > ((speed + 1) * speed) / 2) {
      if (speed < limit) {
        speed += 1;
      }
    } else {
      if (speed > 2) {
        speed -= 1;
      }
    }
    return speed;
  };
}

/**
 * 曲线运动参数求解
 * @param {object} a 坐标
 * @param {object} b 坐标
 * @param {number} r 陡峭度
 * @returns {object} 一元二次函数a,b,c参数值
 */
// 陡峭度
const RADIAN = 0.8;
export function curve(a, b, r = RADIAN) {
  const c = {
    x: (a.x + b.x) / 2,
    y: a.y < b.y ? a.y * r : b.y * r,
  };
  return {
    a:
      a.y / ((a.x - b.x) * (a.x - c.x)) +
      c.y / ((c.x - b.x) * (c.x - a.x)) +
      b.y / ((b.x - c.x) * (b.x - a.x)),
    b:
      (a.y * (-b.x - c.x)) / ((a.x - b.x) * (a.x - c.x)) +
      (c.y * (-b.x - a.x)) / ((c.x - a.x) * (c.x - b.x)) +
      (b.y * (-c.x - a.x)) / ((b.x - a.x) * (b.x - c.x)),
    c:
      (a.y * b.x * c.x) / ((a.x - b.x) * (a.x - c.x)) +
      (c.y * a.x * b.x) / ((c.x - a.x) * (c.x - b.x)) +
      (b.y * a.x * c.x) / ((b.x - a.x) * (b.x - c.x)),
  };
}

/**
 * 执行具体动画的任务
 * @param {function} fn
 * @param {number} time
 */
export function animation(fn, time) {
  return new Promise(resolve => {
    setTimeout(() => {
      fn && fn(resolve);
    }, time);
  });
}

/**
 * 获取元素位置
 * @param {HTMLNode} node
 * @param {boolean} isFixed 是否为fixed定位
 * @param {boolean} toFixed 是否转换为fixed定位
 * @returns {object} 返回节点的x/y坐标
 */
export function getPosition(node, opt) {
  const position = { x: 0, y: 0 };
  while (node && node.tagName && node.tagName !== 'BODY') {
    position.y += node.offsetTop;
    position.x += node.offsetLeft;
    node = node.offsetParent;
  }
  // postion:fixed;的元素需要加上滚动条的距离
  if (opt && opt.isFixed) {
    const s = document.scrollingElement || document.body;
    position.y += s.scrollTop;
    position.x += s.scrollLeft;
  }
  // postion:absolute;的元素转为和fixed的计算
  if (opt && opt.toFixed) {
    const s = document.scrollingElement || document.body;
    position.y -= s.scrollTop;
    position.x -= s.scrollLeft;
  }
  return position;
}

/**
 * 克隆节点并设置默认样式
 * @param {HTMLNode}   node
 * @param {object}     style    样式
 * @param {boolean}    isAppend 是否插入到bodyzhong
 * @returns {HTMLNode} 返回克隆并设置样式的节点
 */
export function cloneNode(node, style, isAppend) {
  const cloneNode = node.cloneNode(true);
  css(cloneNode, style);
  isAppend && document.body.appendChild(cloneNode);
  return cloneNode;
}

/**
 * 通过cssText属性一次性设置节点的样式
 * @param {HTMLNode} node
 * @param {object} style
 */
export function css(node, style) {
  if (node && style) {
    const newStyle = {
      ...parseCssText(node.style.cssText),
      ...style,
    };
    node.style.cssText = stringifyCssText(newStyle);
  }
}

/**
 * 格式化节点的cssText样式,并以键值对的形式返回
 * @param {string} text
 * @returns {object} 返回格式化的键值对对象
 */
export function parseCssText(text) {
  const o = {};
  if (text) {
    const cssArr = text.split(/[ \s]*;[ \s]*/);
    for (const i in cssArr) {
      const kvs = cssArr[i].split(/[ \s]*:[ \s]*/);
      const k = kvs[0];
      const v = kvs[1];
      if (k && v) {
        o[k] = v;
      }
    }
  }
  return o;
}

/**
 * 字符串话节点的cssText样式,并以文本的形式返回
 * @param {object} o
 * @returns {string} 返回字符串化的样式内容
 */
export function stringifyCssText(o) {
  let text = '';
  for (const k in o) {
    const v = o[k];
    text += `${k}:${v};`;
  }
  return text;
}

/**
 * 获取节点样式
 * @param {HTMLNode} el
 * @param {string} styleProp
 * @returns {string} 返回节点样式
 */
export function getStyle(el, styleProp) {
  let value;
  const defaultView = (el.ownerDocument || document).defaultView;
  // W3C standard way:
  if (defaultView && defaultView.getComputedStyle) {
    // sanitize property name to css notation
    // (hypen separated words eg. font-Size)
    styleProp = styleProp.replace(/([A-Z])/g, '-$1').toLowerCase();
    const v = defaultView
      .getComputedStyle(el, null)
      .getPropertyValue(styleProp);
    return v;
  } else if (el.currentStyle) {
    // IE
    // sanitize property name to camelCase
    styleProp = styleProp.replace(/-(\w)/g, function(str, letter) {
      return letter.toUpperCase();
    });
    value = el.currentStyle[styleProp];
    // convert other units to pixels on IE
    if (/^\d+(em|pt|%|ex)?$/i.test(value)) {
      return (function(value) {
        var oldLeft = el.style.left,
          oldRsLeft = el.runtimeStyle.left;
        el.runtimeStyle.left = el.currentStyle.left;
        el.style.left = value || 0;
        value = el.style.pixelLeft + 'px';
        el.style.left = oldLeft;
        el.runtimeStyle.left = oldRsLeft;
        return value;
      })(value);
    }
    return value;
  }
}
